import { Component, OnInit } from '@angular/core';
import { StrategyService } from '../../../../data/services/view/strategy.service';
import { ApiTemplatesService } from '../../../../data/services/api/api-templates.service';
import { SCENARIO_OPTIONS, DAYS_RANGE_LABELS } from '../../../../data/constants/strategies.constants';
import { IMasterTemplate } from '../../../../data/interfaces/master-template.interface';
import { ScenarioEnum } from '../../../../data/enum/scenario.enum';

/**
 * Step 1: Información Básica, Configuración Temporal y Opciones de Ejecución
 * ACTUALIZADO: Usa daysRange en lugar de daysOffset, incluye autoExecute y active
 */
@Component({
  selector: 'app-step1-basic',
  templateUrl: './step1-basic.component.html',
  styleUrls: ['./step1-basic.component.scss']
})
export class Step1BasicComponent implements OnInit {
  templates: IMasterTemplate[] = [];
  scenarioOptions = SCENARIO_OPTIONS;
  previewDate: string = '';
  daysRangeLabels: typeof DAYS_RANGE_LABELS.POST_DUE_DATE = DAYS_RANGE_LABELS.POST_DUE_DATE;

  constructor(
    public strategyService: StrategyService,
    private apiTemplatesService: ApiTemplatesService
  ) {}

  ngOnInit() {
    this.loadTemplates();
    this.updateLabels();
    this.calculatePreview();
    
    // Actualizar labels y preview cuando cambia scenario
    this.form.get('scenario')?.valueChanges.subscribe(() => {
      this.onScenarioChange();
    });
    
    // Actualizar preview cuando cambia daysRange
    const daysRangeForm = this.form.get('daysRange');
    daysRangeForm?.get('minDays')?.valueChanges.subscribe((minDays) => {
      this.onMinDaysChange(minDays);
      this.calculatePreview();
    });
    daysRangeForm?.get('maxDays')?.valueChanges.subscribe((maxDays) => {
      this.onMaxDaysChange(maxDays);
      this.calculatePreview();
    });
    
    // Actualizar templateId de canales activos cuando cambia masterTemplateId
    this.form.get('masterTemplateId')?.valueChanges.subscribe((masterTemplateId) => {
      if (masterTemplateId) {
        this.updateChannelTemplateIds(masterTemplateId);
      }
    });
  }

  /**
   * Cuando cambia minDays, verifica si maxDays sigue siendo válido
   * maxDays debe ser > minDays, sino se convierte en "sin límite"
   */
  onMinDaysChange(minDays: number) {
    const maxDays = this.daysRangeForm?.get('maxDays')?.value;
    
    // Si maxDays tiene valor y es <= minDays, convertir a "sin límite"
    if (maxDays !== null && maxDays !== undefined && maxDays <= minDays) {
      this.daysRangeForm?.get('maxDays')?.setValue(null, { emitEvent: false });
    }
  }

  /**
   * Cuando cambia maxDays, valida que sea > minDays
   * Si es <= minDays, se convierte automáticamente en "sin límite" (null)
   */
  onMaxDaysChange(maxDays: number | null) {
    // Si es null, undefined o 0 = "sin límite", está OK
    if (maxDays === null || maxDays === undefined || maxDays === 0) {
      // Convertir 0 a null para consistencia
      if (maxDays === 0) {
        this.daysRangeForm?.get('maxDays')?.setValue(null, { emitEvent: false });
      }
      return;
    }
    
    const minDays = this.daysRangeForm?.get('minDays')?.value || 0;
    
    // maxDays debe ser estrictamente mayor que minDays
    // Si es <= minDays, convertir a "sin límite"
    if (maxDays <= minDays) {
      this.daysRangeForm?.get('maxDays')?.setValue(null, { emitEvent: false });
    }
  }

  get form() {
    return this.strategyService.entity;
  }

  get isDueDateScenario(): boolean {
    return this.form.get('scenario')?.value === ScenarioEnum.DUE_DATE;
  }

  get isPreDueDateScenario(): boolean {
    return this.form.get('scenario')?.value === ScenarioEnum.PRE_DUE_DATE;
  }

  get isPostDueDateScenario(): boolean {
    return this.form.get('scenario')?.value === ScenarioEnum.POST_DUE_DATE;
  }

  get daysRangeForm() {
    return this.form.get('daysRange');
  }

  loadTemplates() {
    // Cargar plantillas usando cache - solo hace petición HTTP la primera vez
    this.apiTemplatesService.getAllCached().subscribe((templates) => {
      this.templates = templates;
    });
  }

  updateLabels() {
    const scenario = this.form.get('scenario')?.value;
    this.daysRangeLabels = DAYS_RANGE_LABELS[scenario as keyof typeof DAYS_RANGE_LABELS] 
      || DAYS_RANGE_LABELS.POST_DUE_DATE;
  }

  onScenarioChange() {
    const scenario = this.form.get('scenario')?.value;
    const daysRangeForm = this.form.get('daysRange');
    
    // Actualizar labels dinámicos
    this.updateLabels();
    
    // Ajustar valores por defecto según escenario
    switch (scenario) {
      case ScenarioEnum.PRE_DUE_DATE:
        // Para PRE_DUE_DATE: Solo un día específico (minDays = maxDays)
        // Es un recordatorio, no queremos ser insistentes
        daysRangeForm?.patchValue({ minDays: 15, maxDays: 15 });
        break;
      case ScenarioEnum.DUE_DATE:
        // DUE_DATE es exactamente día 0
        daysRangeForm?.patchValue({ minDays: 0, maxDays: 0 });
        break;
      case ScenarioEnum.POST_DUE_DATE:
        // Para POST_DUE_DATE: Rango de días (cobranza)
        daysRangeForm?.patchValue({ minDays: 1, maxDays: 30 });
        break;
    }
    
    this.calculatePreview();
  }

  calculatePreview() {
    const scenario = this.form.get('scenario')?.value;
    
    // IMPORTANTE: Leer valores directamente de los controles individuales
    // para evitar desfases de sincronización con el FormGroup padre
    const minDays = this.daysRangeForm?.get('minDays')?.value;
    const maxDays = this.daysRangeForm?.get('maxDays')?.value ?? null;
    
    if (minDays === null || minDays === undefined) {
      this.previewDate = '';
      return;
    }
    
    // Para PRE_DUE_DATE: Solo se usa minDays (día específico)
    // El backend recibirá minDays = maxDays para PRE_DUE_DATE
    if (scenario === ScenarioEnum.PRE_DUE_DATE) {
      this.previewDate = this.strategyService.getScenarioDisplayText(
        scenario,
        { minDays: minDays, maxDays: minDays }
      );
      return;
    }
    
    // Para POST_DUE_DATE: Usar el rango completo
    this.previewDate = this.strategyService.getScenarioDisplayText(
      scenario,
      { minDays, maxDays }
    );
  }

  /**
   * Actualiza el templateId de todos los canales activos cuando cambia masterTemplateId
   */
  updateChannelTemplateIds(masterTemplateId: string) {
    const channelsForm = this.form.get('channels') as any;
    if (!channelsForm) return;

    const channels = ['email', 'sms', 'whatsapp', 'call'];
    channels.forEach(channel => {
      const channelForm = channelsForm.get(channel);
      if (channelForm && channelForm.get('enabled')?.value) {
        channelForm.get('templateId')?.setValue(masterTemplateId);
      }
    });
  }
}
